import { JobCardVo, JobFitValues } from '@components/job-card/job-card-vo';
import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { categoryMapping, generateJobDetail } from '@pages/jobs/utils/jobs-detail.util';
import { jobFit } from '../job-fit';
import { getTopTwoTraitIds } from '../utils/get-top-two-traits.util';
import { roleCredentialTier, userPlannedCredentialTier } from '../utils/credential-tier.util';
import { buildTfIdfOverlapSource } from '../utils/tfidf-overlap.util';
import { mergeExperienceUserOccupations } from './merge-experience-user-occupations.helper';
import { JobMatchState } from './reducers';

export const jobMatchStateSelector = createFeatureSelector<JobMatchState>('job-match');

export const jobsLoadingSelector = createSelector(jobMatchStateSelector, state => state.jobsLoading);
export const filterSelector = createSelector(jobMatchStateSelector, state => state.filter);
export const jobsSelector = createSelector(jobMatchStateSelector, state => state?.jobs || []);
export const allOccupationsSelector = createSelector(jobMatchStateSelector, state => state.occupations || []);
export const userTraitScoresSelector = createSelector(jobMatchStateSelector, state => state.userTraitScores ?? {});
export const occupationsOnetCodesSelector = createSelector(
  jobMatchStateSelector,
  state => state.skillsSurvey?.occupations?.map(occupation => occupation.id) || []
);
export const degreeProgramCipCodesSelector = createSelector(
  jobMatchStateSelector,
  state => state.skillsSurvey?.majors?.map(major => major.degreeProgram?.id) || []
);

// Build the previousJobs source: the user's own selected work-experience
// occupations, auto-graded as greatFit. This is an experience source (the
// user has explicitly told us they've done this kind of work).
const buildPreviousJobsSource = (state: JobMatchState | undefined): any[] => {
  const previousJobs = [
    {
      __typename: ' MatchTypeDistribution',
      greatFit: [] as any[],
      goodFit: [] as any[],
      okFit: [] as any[]
    }
  ];
  state?.skillsSurvey?.occupations.forEach(job => {
    previousJobs[0].greatFit.push({
      __typename: 'JobFamilyOccupation',
      onetCode: job.id
    });
  });
  return previousJobs;
};

/**
 * Experience-only skill sources: work-experience job-family (which also
 * carries military O*NET codes — both feed into the same gateway query),
 * the user's own selected occupations, and the synthetic TF-IDF KSA-overlap
 * source computed from the user's input occupations' KSAs against each
 * banking job's KSAs. These sources represent what the user has ACTUALLY DONE.
 *
 * Used by the credential-aware skills-match path. GREAT requires
 * experience-signal === 'strong'.
 */
export const experienceSourcesSelector = createSelector(jobMatchStateSelector, state => {
  if (!state) {
    return [];
  }
  // User-input occupations with their raw KSAs live in two slices:
  //   - state.skillsSurvey.occupationsDetails (civilian work, populated by
  //     occupationsFetchSuccess via the loadOccupations effect)
  //   - state.militaryOccupations (military MOS crosswalk, populated by
  //     militaryOccupationsFetchSuccess)
  // Both reducers store the full `{ ksa: {id}, importance }` raw shape under
  // `rawOccupationKSAs`. The merge helper concatenates them into the single
  // input list expected by buildTfIdfOverlapSource so military experience
  // contributes to KSA-overlap just like civilian experience.
  const userInputOccupations = mergeExperienceUserOccupations(
    state.skillsSurvey?.occupationsDetails,
    state.militaryOccupations
  );
  const tfidfOverlapSource = buildTfIdfOverlapSource(userInputOccupations, state.bankingJobKsas ?? {});
  return [
    ...state.jobFamilyOccupations.map(item => item.jobFamilyOccupations),
    ...buildPreviousJobsSource(state),
    tfidfOverlapSource
  ];
});

/**
 * CIP-only skill sources: degreeProgramOccupations from the user's program
 * of study. Represents what the user is STUDYING — informative but not
 * sufficient evidence of work capability.
 *
 * Used by the credential-aware skills-match path. CIP-strong at exact
 * credential earns GOOD (not GREAT).
 */
export const cipSourcesSelector = createSelector(jobMatchStateSelector, state => {
  if (!state) {
    return [];
  }
  return state.degreeProgramOccupations.map(item => item.distribution);
});

/**
 * Combined skill sources (legacy shape). Retained for callers that don't
 * separate experience from CIP (e.g. my-fit.component.ts, which calls jobFit
 * without credential tiers). When the credential-aware path runs, the
 * individual experience/CIP sources are used; this combined version is the
 * legacy fallback.
 *
 * `educationLevelOccupations` was previously merged here as a skill source
 * but was removed in PR #256 — it's really credential information, not skill.
 */
export const occupationsSelector = createSelector(jobMatchStateSelector, state => {
  if (!state) {
    return undefined;
  }
  return [
    ...state.jobFamilyOccupations.map(item => item.jobFamilyOccupations),
    ...state.degreeProgramOccupations.map(item => item.distribution),
    ...buildPreviousJobsSource(state)
  ];
});

export const occupationListSelector = createSelector(jobMatchStateSelector, state => {
  if (!state) {
    return [];
  }

  return state.skillsSurvey.occupations.map(({ id, name }) => ({ id, name }));
});

export const selectedOccupationSelector = createSelector(
  jobMatchStateSelector,
  state => state.skillsSurvey.selectedOccupation
);

export const selectedOccupationKSAsSelector = createSelector(jobMatchStateSelector, state => {
  if (!state) {
    return [];
  }

  return getSkillsOccupation(state.skillsSurvey.occupationsDetails, state.skillsSurvey.selectedOccupation);
});

export const matchedJobsSelector = createSelector(
  jobMatchStateSelector,
  experienceSourcesSelector,
  cipSourcesSelector,
  (state, experienceSources, cipSources) => {
    const userTier = userPlannedCredentialTier(state.skillsSurvey?.majors);
    return state.jobs
      .map(job => {
        const occupation = state.occupations?.find(o => o?.onetCode === (job.custom_id || job.onet_code));
        const roleTier = roleCredentialTier(occupation?.typicalEducationLevelGroup);
        return {
          ...job,
          fit: jobFit(
            state.filter,
            job.onet_code,
            job.interestScore,
            experienceSources,
            state.userTraitScores ?? {},
            getTopTwoTraitIds(occupation),
            userTier,
            roleTier,
            cipSources
          )
        };
      })
      .filter(job => job.fit);
  }
);

export const matchedAndPaginatedJobsSelector = (props: {
  start: number;
  end: number;
  filterType?: string;
}): MemoizedSelector<object, { jobs: JobCardVo[]; totalItem: number }> =>
  createSelector(
    jobMatchStateSelector,
    experienceSourcesSelector,
    cipSourcesSelector,
    (state, experienceSources, cipSources) => {
      const filterState = props?.filterType ?? state.filter;
      // Null-coalesce both sources: localStorage rehydration can legitimately
      // leave these fields undefined before reducers re-populate them, and the
      // `.map` below would otherwise crash and tear down the selector stream.
      const jobs = filterState === 'skills-match' ? state.jobs ?? [] : state.lastInterestMatchedJobs ?? [];
      const userTier = userPlannedCredentialTier(state.skillsSurvey?.majors);
      const results = (
        jobs
          .map(job => {
            const occupation = state.occupations?.find(o => o?.onetCode === (job.custom_id || job.onet_code));
            const roleTier = roleCredentialTier(occupation?.typicalEducationLevelGroup);
            return {
              ...job,
              ...generateJobDetail(job, occupation),
              fit: jobFit(
                filterState,
                job.onet_code,
                job.interestScore,
                experienceSources,
                state.userTraitScores ?? {},
                getTopTwoTraitIds(occupation),
                userTier,
                roleTier,
                cipSources
              )
            };
          })
          .filter(job => job.fit) as JobCardVo[]
      ).sort((firstJob, secondJob) => (JobFitValues[firstJob.fit] < JobFitValues[secondJob.fit] ? -1 : 1));
      return {
        jobs: results.slice(props.start, props.end),
        totalItem: results.length
      };
    }
  );

export const interestsLoadingSelector = createSelector(jobMatchStateSelector, state => state.interestsLoading);
export const interestsSelector = createSelector(jobMatchStateSelector, state => state.interests || []);
export const lastInterestMatchedJobsSelector = createSelector(jobMatchStateSelector, state => ({
  interests: state.interests,
  lastInterestMatchedJobs: state.lastInterestMatchedJobs
}));

export const lastSkillMatchedJobsSelector = createSelector(jobMatchStateSelector, state => ({
  skills: state.skills,
  skillsSurvey: state.skillsSurvey,
  jobFamilyOccupations: state.jobFamilyOccupations,
  degreeProgramOccupations: state.degreeProgramOccupations,
  educationLevelOccupations: state.educationLevelOccupations,
  militaryOccupations: state.militaryOccupations
}));

export const interestsSurveyCurrentQuestionIndexSelector = createSelector(
  jobMatchStateSelector,
  state => state.interestsSurvey.currentQuestionIndex
);
export const interestsSurveyCurrentQuestionSelector = createSelector(
  jobMatchStateSelector,
  state => state.interests[state.interestsSurvey.currentQuestionIndex]
);

export const interestsSurveySmileQuestionSelector = createSelector(jobMatchStateSelector, state =>
  state?.interests.filter(q => q.answer === 'SMILE')
);
export const interestsSurveyFrownQuestionSelector = createSelector(jobMatchStateSelector, state =>
  state?.interests.filter(q => q.answer === 'FROWN')
);
export const interestsSurveyMehQuestionSelector = createSelector(jobMatchStateSelector, state =>
  state?.interests.filter(q => q.answer === 'MEH')
);

export const interestsSurveySmileQuestionWithCategorySelector = createSelector(jobMatchStateSelector, state =>
  state?.interests
    .filter(q => q.answer === 'SMILE')
    .map(question => ({
      ...question,
      category: categoryMapping.get(question.reviewText)
    }))
);
export const reviewInterestsSurveySmileQuestionSelector = createSelector(
  jobMatchStateSelector,
  interestsSurveySmileQuestionSelector,
  (state, interestsSurveySmileQuestions) =>
    state?.reviewInterests?.length ? state?.reviewInterests : interestsSurveySmileQuestions
);
export const interestsSurveyQuestionCountSelector = createSelector(
  jobMatchStateSelector,
  state => state.interests.length
);
export const interestsSurveySmileQuestionCountSelector = createSelector(
  interestsSurveySmileQuestionSelector,
  questions => questions.length
);
export const interestsSurveyFrownQuestionCountSelector = createSelector(
  interestsSurveyFrownQuestionSelector,
  questions => questions.length
);
export const interestsSurveyMehQuestionCountSelector = createSelector(
  interestsSurveyMehQuestionSelector,
  questions => questions.length
);

export const militaryOccupationsOnetCodesSelector = createSelector(
  jobMatchStateSelector,
  state => state.militaryOccupations?.map(occupation => occupation?.onetCode) || []
);

export const militaryOccupationsSkillsSelector = createSelector(jobMatchStateSelector, state => {
  if (!state) {
    return [];
  }

  return getSkillsOccupation(state.militaryOccupations, state.militaryOccupations[0]?.onetCode);
});

export const surveysCompletionSelector = createSelector(jobMatchStateSelector, state => ({
  interest: state.interests.length && (state.interests.every(q => !!q.answer) || state.interestsSurvey.takeOnce),
  skills: state.skills.length > 0 || state.skillsSurvey.takeOnce,
  military: state.militaryOccupations?.length > 0
}));

export const skillsSelector = createSelector(jobMatchStateSelector, state => state?.skills);

export const getSkillsOccupation = (occupationsDetails, selectedOccupation): any[] => {
  const selected: any = occupationsDetails.find(item => item.onetCode === selectedOccupation);

  if (!selected) {
    return [];
  }

  return selected.occupationKSAs || [];
};
